import * as signalR from '@microsoft/signalr';

class SignalRService {
  connection: signalR.HubConnection | null = null;

  private connectionPromise: Promise<void> | null = null;

  private readonly subscribedGroups = new Set<string>();

  startConnection = async (hubUrl: string, apiKey?: string) => {
    // Already connected
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    // Connection already in progress
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (!this.connection) {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${hubUrl}?x-api-key=${encodeURIComponent(apiKey as string)}`)
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.registerReconnectHandlers();
    }

    this.connectionPromise = this.connection
      .start()
      .then(() => {
        console.log('SignalR connected');
      })
      .catch((err) => {
        console.error('SignalR connection error:', err);

        this.connection = null;
        throw err;
      })
      .finally(() => {
        this.connectionPromise = null;
      });

    return this.connectionPromise;
  };

  stopConnection = async () => {
    if (this.connection) {
      await this.connection.stop();

      this.connection = null;
      this.connectionPromise = null;
      this.subscribedGroups.clear();

      console.log('SignalR disconnected');
    }
  };

  on = (eventName: string, callback: (...args: any[]) => void) => {
    this.connection?.on(eventName, callback);
  };

  off = (eventName: string) => {
    this.connection?.off(eventName);
  };

  invoke = async (methodName: string, ...args: any[]) => {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      throw new Error(
        `SignalR connection is not connected. Current state: ${this.connection?.state}`
      );
    }

    return this.connection.invoke(methodName, ...args);
  };

  subscribeToGroup = async (groupName: string) => {
    if (this.subscribedGroups.has(groupName)) {
      console.log(`Already subscribed to group: ${groupName}`);
      return;
    }

    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      throw new Error(
        `Cannot subscribe. Connection state: ${this.connection?.state}`
      );
    }

    await this.connection.invoke('SubscribeToGroup', groupName);

    this.subscribedGroups.add(groupName);

    console.log(`Subscribed to group: ${groupName}`);
    console.log('subscribedGroups: ', this.subscribedGroups);
  };

  unsubscribeFromGroup = async (groupName: string) => {
    if (!this.subscribedGroups.has(groupName)) {
      console.log(`Group not subscribed: ${groupName}`);
      return;
    }

    this.subscribedGroups.delete(groupName);

    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke('UnsubscribeFromGroup', groupName);

      console.log(`Unsubscribed from group: ${groupName}`);
      console.log('subscribedGroups: ', this.subscribedGroups);
    } catch (err) {
      console.error(`Failed to unsubscribe from ${groupName}`, err);
    }
  };

  private registerReconnectHandlers() {
    this.connection?.onreconnecting((error) => {
      console.warn('SignalR reconnecting...', error);
    });

    this.connection?.onreconnected(async () => {
      console.log('SignalR reconnected');

      for (const group of this.subscribedGroups) {
        try {
          await this.connection?.invoke('SubscribeToGroup', group);

          console.log(`Re-subscribed to group: ${group}`);
        } catch (err) {
          console.error(`Failed to re-subscribe group ${group}`, err);
        }
      }
    });

    this.connection?.onclose((err) => {
      console.warn('SignalR connection closed', err);
    });
  }
}

export const signalRService = new SignalRService();
